type FormikErrors<T> = {
  [K in keyof T]?: string | FormikErrors<T[K]>;
};

export interface TabFieldsConfig<T = any> {
  [tabValue: number | string]: (keyof T)[];
}

export interface RequiredTabsConfig {
  [tabValue: number | string]: boolean;
}

export const useTabErrors = <T extends Record<string, any>>(
  errors: FormikErrors<T>,
  tabFieldsConfig: TabFieldsConfig<T>,
  requiredTabsConfig: RequiredTabsConfig = {},
  setFieldTouched?: (field: string, touched?: boolean, shouldValidate?: boolean) => void
) => {
  const getErrorCountForTab = (tabValue: number | string): number => {
    const fieldsForTab = tabFieldsConfig[tabValue] || [];
    return fieldsForTab.reduce((count, fieldName) => {
      return errors[fieldName] ? count + 1 : count;
    }, 0);
  };

  const getTabNotification = (tabValue: number | string): string | undefined => {
    const errorCount = getErrorCountForTab(tabValue);
    const isRequired = requiredTabsConfig[tabValue];

    // Si il y a des erreurs, afficher le nombre d'erreurs
    if (errorCount > 0) {
      return errorCount.toString();
    }

    // Si l'onglet est obligatoire ET qu'il n'y a pas d'erreurs, ne pas afficher la notification
    // (cela signifie que l'onglet obligatoire a été correctement rempli)
    if (isRequired && errorCount === 0) {
      return undefined;
    }

    // Si c'est un onglet obligatoire mais qu'on ne peut pas déterminer s'il y a des erreurs
    // (par exemple au chargement initial), afficher "!"
    if (isRequired) {
      return "!";
    }

    return undefined;
  };

  const getTotalErrors = (): number => {
    return Object.keys(tabFieldsConfig).reduce((total, tabValue) => {
      return total + getErrorCountForTab(tabValue);
    }, 0);
  };

  const hasErrorsInTab = (tabValue: number | string): boolean => {
    return getErrorCountForTab(tabValue) > 0;
  };

  // Marque tous les champs avec erreurs comme touched pour afficher les bordures rouges
  const touchFieldsWithErrors = (): void => {
    if (!setFieldTouched) return;

    Object.keys(errors).forEach((fieldName) => {
      if (errors[fieldName as keyof T]) {
        setFieldTouched(fieldName, true, false);
      }
    });
  };

  // Marque tous les champs d'un onglet spécifique avec erreurs comme touched
  const touchFieldsWithErrorsInTab = (tabValue: number | string): void => {
    if (!setFieldTouched) return;

    const fieldsForTab = tabFieldsConfig[tabValue] || [];
    fieldsForTab.forEach((fieldName) => {
      if (errors[fieldName]) {
        setFieldTouched(fieldName as string, true, false);
      }
    });
  };

  return {
    getErrorCountForTab,
    getTabNotification,
    getTotalErrors,
    hasErrorsInTab,
    touchFieldsWithErrors,
    touchFieldsWithErrorsInTab
  };
};
